import React, { useState } from 'react';
import { 
  FaPlus, FaEdit, FaTrash, FaCheckCircle, FaCalendarDay, 
  FaTimes, FaChevronLeft, FaChevronRight, FaCalendarAlt 
} from 'react-icons/fa';
import Last3MonthsEarningsCard from './Last3MonthsEarningsCard';
import { 
  getStreamMonthData, 
  getYearMonth, 
  formatMonthYear, 
  parseDateParts, 
  getTodayDateStr, 
  generateMondaysForMonth 
} from './billTrackerData';

const IncomeSheet = ({ incomeStreams, setIncomeStreams, config, setConfig }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingStream, setEditingStream] = useState(null);
  const [mondayModalStream, setMondayModalStream] = useState(null);

  // Active viewing month derived from config.referenceDate or today
  const activeRefDate = config?.referenceDate || getTodayDateStr();
  const activeYearMonth = getYearMonth(activeRefDate);
  const activeMonthLabel = formatMonthYear(activeYearMonth);
  const isViewingToday = activeRefDate === getTodayDateStr();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    expectedAmount: '',
    actualReceived: '',
    frequency: 'Monthly'
  });

  // Calculate totals for the active viewing month
  const totalExpected = incomeStreams.reduce((sum, s) => {
    const data = getStreamMonthData(s, activeYearMonth);
    return sum + (data.expectedAmount || 0);
  }, 0);

  const totalReceived = incomeStreams.reduce((sum, s) => {
    const data = getStreamMonthData(s, activeYearMonth);
    return sum + (data.actualReceived || 0);
  }, 0);

  const portfolioRealization = totalExpected > 0 ? Math.round((totalReceived / totalExpected) * 100) : 0;

  // Month navigation: Prev Month / Next Month
  const handleShiftMonth = (offset) => {
    const { year, month } = parseDateParts(activeRefDate);
    const nextDate = new Date(year, month - 1 + offset, 1);
    const nextYear = nextDate.getFullYear();
    const nextMonth = String(nextDate.getMonth() + 1).padStart(2, '0');
    const newRefDate = `${nextYear}-${nextMonth}-01`;
    if (setConfig) {
      setConfig(prev => ({
        ...prev,
        referenceDate: newRefDate,
        autoSyncRealDate: false
      }));
    }
  };

  // Direct selection of a specific month (e.g. from the 3-month card)
  const handleSelectMonth = (monthKey) => {
    const [y, m] = monthKey.split('-').map(Number);
    const newRefDate = `${y}-${String(m).padStart(2, '0')}-01`;
    if (setConfig) {
      setConfig(prev => ({
        ...prev,
        referenceDate: newRefDate,
        autoSyncRealDate: false
      }));
    }
  };

  // Return to today's real calendar date
  const handleResetToToday = () => {
    if (setConfig) {
      setConfig(prev => ({
        ...prev,
        referenceDate: getTodayDateStr(),
        autoSyncRealDate: true
      }));
    }
  };

  // Open add modal
  const handleOpenAdd = () => {
    setEditingStream(null);
    setFormData({
      name: '',
      description: '',
      expectedAmount: '',
      actualReceived: '0',
      frequency: 'Monthly'
    });
    setIsAddModalOpen(true);
  };

  // Open edit modal
  const handleOpenEdit = (stream) => {
    setEditingStream(stream);
    const monthData = getStreamMonthData(stream, activeYearMonth);
    setFormData({
      name: stream.name,
      description: stream.description || '',
      expectedAmount: monthData.expectedAmount,
      actualReceived: monthData.actualReceived,
      frequency: stream.frequency || 'Monthly'
    });
    setIsAddModalOpen(true);
  };

  // Delete stream
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to remove this income stream?')) {
      setIncomeStreams(prev => prev.filter(s => s.id !== id));
    }
  };

  // Save new / edited stream
  const handleSaveStream = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.expectedAmount) return;

    const expAmt = parseFloat(formData.expectedAmount) || 0;
    const actAmt = parseFloat(formData.actualReceived) || 0;

    if (editingStream) {
      setIncomeStreams(prev => prev.map(s => {
        if (s.id === editingStream.id) {
          const history = { ...(s.monthlyHistory || {}) };
          const activeHistory = history[activeYearMonth] || {};
          const currentMondays = activeHistory.mondays || s.mondays;
          const count = currentMondays?.length || 4;
          const weeklyExp = Math.round((expAmt / count) * 100) / 100;
          const weeklyAct = actAmt > 0 ? Math.round((actAmt / count) * 100) / 100 : 0;

          const updatedMondays = currentMondays ? currentMondays.map((m, idx) => ({
            ...m,
            expected: weeklyExp,
            actual: idx === count - 1 && actAmt > 0 ? Math.round((actAmt - (weeklyAct * (count - 1))) * 100) / 100 : weeklyAct,
            status: weeklyAct >= weeklyExp ? 'received' : (weeklyAct > 0 ? 'partial' : 'expected')
          })) : generateMondaysForMonth(activeYearMonth, expAmt, actAmt);

          history[activeYearMonth] = {
            expectedAmount: expAmt,
            actualReceived: actAmt,
            mondays: updatedMondays
          };

          return {
            ...s,
            name: formData.name,
            description: formData.description,
            frequency: formData.frequency,
            expectedAmount: expAmt,
            actualReceived: actAmt,
            mondays: updatedMondays,
            monthlyHistory: history
          };
        }
        return s;
      }));
    } else {
      const newId = `inc-${Date.now()}`;
      const mondays = generateMondaysForMonth(activeYearMonth, expAmt, actAmt);
      const history = {
        [activeYearMonth]: {
          expectedAmount: expAmt,
          actualReceived: actAmt,
          mondays
        }
      };

      const newStream = {
        id: newId,
        name: formData.name,
        description: formData.description,
        frequency: formData.frequency || 'Monthly',
        expectedAmount: expAmt,
        actualReceived: actAmt,
        mondays,
        monthlyHistory: history
      };
      setIncomeStreams(prev => [...prev, newStream]);
    }

    setIsAddModalOpen(false);
  };

  // Update a single Monday week inside the 4-week modal
  const handleUpdateMonday = (streamId, weekIndex, newActual) => {
    const val = parseFloat(newActual) || 0;

    setIncomeStreams(prev => prev.map(s => {
      if (s.id === streamId) {
        const history = { ...(s.monthlyHistory || {}) };
        const monthData = history[activeYearMonth] || getStreamMonthData(s, activeYearMonth);
        const updatedMondays = [...(monthData.mondays || [])];

        if (updatedMondays[weekIndex]) {
          updatedMondays[weekIndex] = {
            ...updatedMondays[weekIndex],
            actual: val,
            status: val >= (updatedMondays[weekIndex].expected || 0) ? 'received' : (val > 0 ? 'partial' : 'expected')
          };
        }

        const sumActual = updatedMondays.reduce((acc, m) => acc + (m.actual || 0), 0);

        history[activeYearMonth] = {
          ...monthData,
          actualReceived: sumActual,
          mondays: updatedMondays
        };

        return {
          ...s,
          actualReceived: sumActual,
          mondays: updatedMondays,
          monthlyHistory: history
        };
      }
      return s;
    }));

    // also sync mondayModalStream in place
    if (mondayModalStream && mondayModalStream.id === streamId) {
      setMondayModalStream(prev => {
        const history = { ...(prev.monthlyHistory || {}) };
        const monthData = history[activeYearMonth] || getStreamMonthData(prev, activeYearMonth);
        const updatedMondays = [...(monthData.mondays || [])];

        if (updatedMondays[weekIndex]) {
          updatedMondays[weekIndex] = {
            ...updatedMondays[weekIndex],
            actual: val,
            status: val >= (updatedMondays[weekIndex].expected || 0) ? 'received' : (val > 0 ? 'partial' : 'expected')
          };
        }

        const sumActual = updatedMondays.reduce((acc, m) => acc + (m.actual || 0), 0);

        history[activeYearMonth] = {
          ...monthData,
          actualReceived: sumActual,
          mondays: updatedMondays
        };

        return {
          ...prev,
          actualReceived: sumActual,
          mondays: updatedMondays,
          monthlyHistory: history
        };
      });
    }
  };

  return (
    <div>
      {/* Top Header & Actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '20px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800 }}>
            INFLOW & REVENUE STREAMS
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.86rem' }}>
            Manage recurring revenues, monthly actual receipts, and historical inflow performance
          </p>
        </div>
        <button className="bt-btn-primary" onClick={handleOpenAdd}>
          <FaPlus /> Add Revenue Stream
        </button>
      </div>

      {/* 3-Month Earnings Activity Comparison Card */}
      <Last3MonthsEarningsCard
        incomeStreams={incomeStreams}
        referenceDate={activeRefDate}
        onSelectMonth={handleSelectMonth}
      />

      {/* Month Navigation Toolbar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '10px 16px',
        marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            className="bt-btn-secondary"
            style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleShiftMonth(-1)}
            title="View previous month income"
          >
            <FaChevronLeft size={10} /> Prev Month
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 8px' }}>
            <FaCalendarAlt style={{ color: '#10b981' }} />
            <strong style={{ fontSize: '0.95rem', color: '#0f172a' }}>{activeMonthLabel}</strong>
            <span style={{
              fontSize: '0.75rem',
              backgroundColor: isViewingToday ? '#d1fae5' : '#f1f5f9',
              color: isViewingToday ? '#065f46' : '#475569',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '6px'
            }}>
              {isViewingToday ? 'Current Month (Refreshed)' : 'Historical Month'}
            </span>
          </div>

          <button
            className="bt-btn-secondary"
            style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={() => handleShiftMonth(1)}
            title="Advance to next month"
          >
            Next Month <FaChevronRight size={10} />
          </button>

          {!isViewingToday && (
            <button
              className="bt-btn-secondary"
              style={{ padding: '6px 10px', fontSize: '0.8rem', color: '#059669', borderColor: '#a7f3d0', fontWeight: 600 }}
              onClick={handleResetToToday}
              title="Return to today's real calendar date"
            >
              Today
            </button>
          )}
        </div>

        <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
          Viewing Income Data for: <strong style={{ color: '#0f172a' }}>{activeMonthLabel}</strong>
        </div>
      </div>

      {/* Streams Table */}
      <div className="bt-table-container" style={{ marginBottom: '24px' }}>
        <table className="bt-table">
          <thead>
            <tr className="bt-table-header">
              <th>REVENUE STREAM</th>
              <th style={{ textAlign: 'right' }}>EXPECTED AMOUNT ($)</th>
              <th style={{ textAlign: 'right' }}>
                ACTUAL RECEIVED ($)
                <div style={{ fontSize: '0.68rem', fontWeight: 500, color: '#94a3b8' }}>{activeMonthLabel}</div>
              </th>
              <th style={{ textAlign: 'center' }}>FREQUENCY</th>
              <th style={{ width: '220px' }}>REALIZATION RATE</th>
              <th style={{ textAlign: 'center' }}>MONDAY 4-WK</th>
              <th style={{ textAlign: 'center' }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {incomeStreams.map((stream) => {
              const monthData = getStreamMonthData(stream, activeYearMonth);
              const expectedAmt = monthData.expectedAmount;
              const actualRec = monthData.actualReceived;
              const rate = expectedAmt > 0
                ? Math.min(100, Math.round((actualRec / expectedAmt) * 100))
                : 0;

              return (
                <tr key={stream.id} className="bt-table-row">
                  {/* Title & Description */}
                  <td className="bt-table-cell">
                    <div className="bt-title-cell">{stream.name}</div>
                    {stream.description && (
                      <div className="bt-subtext">{stream.description}</div>
                    )}
                  </td>

                  {/* Expected */}
                  <td className="bt-table-cell" style={{ textAlign: 'right', fontWeight: 600 }}>
                    ${expectedAmt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>

                  {/* Actual */}
                  <td className="bt-table-cell" style={{ textAlign: 'right', fontWeight: 700, color: actualRec > 0 ? '#059669' : '#64748b' }}>
                    ${actualRec.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {actualRec === 0 && (
                      <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 500 }}>Refreshed ($0.00)</div>
                    )}
                  </td>

                  {/* Frequency */}
                  <td className="bt-table-cell" style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '0.86rem', color: '#475569', fontWeight: 500 }}>
                      {stream.frequency || 'Monthly'}
                    </span>
                  </td>

                  {/* Realization Bar */}
                  <td className="bt-table-cell">
                    <div className="bt-progress-wrapper">
                      <div className="bt-progress-track">
                        <div
                          className={`bt-progress-fill ${rate >= 100 ? 'green' : (rate > 0 ? 'purple' : 'gray')}`}
                          style={{ width: `${rate}%` }}
                        />
                      </div>
                      <span className="bt-progress-text">{rate}%</span>
                    </div>
                  </td>

                  {/* 4-Week Monday View */}
                  <td className="bt-table-cell" style={{ textAlign: 'center' }}>
                    <button
                      className="bt-badge"
                      style={{
                        background: '#f8fafc',
                        border: '1px solid #cbd5e1',
                        color: '#475569',
                        cursor: 'pointer',
                        padding: '4px 8px'
                      }}
                      title={`View ${activeMonthLabel} Monday Breakdown`}
                      onClick={() => setMondayModalStream(stream)}
                    >
                      <FaCalendarDay style={{ color: '#4f46e5' }} /> 4 Mondays
                    </button>
                  </td>

                  {/* Actions */}
                  <td className="bt-table-cell" style={{ textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', gap: '6px' }}>
                      <button
                        className="bt-icon-btn"
                        title="Edit Stream"
                        onClick={() => handleOpenEdit(stream)}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        className="bt-icon-btn danger"
                        title="Delete Stream"
                        onClick={() => handleDelete(stream.id)}
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer Portfolio Realization Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '20px',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: '18px 24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#059669', fontWeight: 700, fontSize: '0.95rem' }}>
          <FaCheckCircle size={18} />
          <span>{activeMonthLabel} Portfolio Realization: {portfolioRealization}%</span>
        </div>

        <div style={{ display: 'flex', gap: '28px', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>
              {activeMonthLabel} Expected
            </span>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
              ${totalExpected.toLocaleString(undefined, { minimumFractionDigits: 0 })}
            </div>
          </div>

          <div>
            <span style={{ fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>
              {activeMonthLabel} Received
            </span>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: totalReceived > 0 ? '#059669' : '#64748b' }}>
              ${totalReceived.toLocaleString(undefined, { minimumFractionDigits: 0 })}
            </div>
          </div>
        </div>
      </div>

      {/* Add / Edit Stream Modal */}
      {isAddModalOpen && (
        <div className="bt-modal-backdrop" onClick={() => setIsAddModalOpen(false)}>
          <div className="bt-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="bt-modal-header">
              <h3>{editingStream ? `Edit Revenue Stream (${activeMonthLabel})` : `Add Revenue Stream (${activeMonthLabel})`}</h3>
              <button className="bt-icon-btn" onClick={() => setIsAddModalOpen(false)}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveStream}>
              <div className="bt-modal-body">
                <div className="bt-form-group">
                  <label>Revenue Stream Name *</label>
                  <input
                    type="text"
                    className="bt-input"
                    placeholder="e.g. Business Gross Revenue or Consulting"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="bt-form-group">
                  <label>Description / Contract Details</label>
                  <input
                    type="text"
                    className="bt-input"
                    placeholder="e.g. Main SaaS subscriptions & merchant deposits"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                <div className="bt-form-row">
                  <div className="bt-form-group">
                    <label>Monthly Expected Amount ($) *</label>
                    <input
                      type="number"
                      step="0.01"
                      className="bt-input"
                      placeholder="12500"
                      value={formData.expectedAmount}
                      onChange={(e) => setFormData({ ...formData, expectedAmount: e.target.value })}
                      required
                    />
                  </div>

                  <div className="bt-form-group">
                    <label>Actual Received to Date ($) for {activeMonthLabel}</label>
                    <input
                      type="number"
                      step="0.01"
                      className="bt-input"
                      placeholder="0"
                      value={formData.actualReceived}
                      onChange={(e) => setFormData({ ...formData, actualReceived: e.target.value })}
                    />
                  </div>
                </div>

                <div className="bt-form-group">
                  <label>Deposit Frequency</label>
                  <select
                    className="bt-input"
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  >
                    <option value="Monthly">Monthly (Divided across 4 Mondays)</option>
                    <option value="Bi-Weekly">Bi-Weekly</option>
                    <option value="Weekly">Weekly</option>
                  </select>
                </div>
              </div>

              <div className="bt-modal-footer">
                <button
                  type="button"
                  className="bt-btn-secondary"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="bt-btn-primary">
                  {editingStream ? 'Save Changes' : 'Create Stream'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4-Week Monday Breakdown Modal */}
      {mondayModalStream && (() => {
        const streamMonthData = getStreamMonthData(mondayModalStream, activeYearMonth);
        const mondays = streamMonthData.mondays || [];

        return (
          <div className="bt-modal-backdrop" onClick={() => setMondayModalStream(null)}>
            <div className="bt-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
              <div className="bt-modal-header">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span className="bt-tuesday-badge" style={{ background: '#ecfdf5', color: '#059669' }}>
                      <FaCalendarDay /> Monday Schedule &bull; {activeMonthLabel}
                    </span>
                  </div>
                  <h3 style={{ margin: 0 }}>{mondayModalStream.name}</h3>
                  <div className="bt-subtext">Income disbursed every Monday (4 weeks for {activeMonthLabel})</div>
                </div>
                <button className="bt-icon-btn" onClick={() => setMondayModalStream(null)}>
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="bt-modal-body">
                <p style={{ fontSize: '0.88rem', color: '#475569', marginTop: 0 }}>
                  Track weekly receipts entering your bank account every <strong>Monday</strong> to ensure liquidity ahead of Tuesday bill payments.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {mondays.map((m, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '14px 18px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        gap: '12px'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>Week {m.week} (Monday)</span>
                          <span style={{ fontSize: '0.78rem', color: '#64748b' }}>{m.date}</span>
                        </div>
                        <div className="bt-subtext">
                          Expected: ${m.expected.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Actual: $</span>
                        <input
                          type="number"
                          step="0.01"
                          className="bt-input"
                          style={{ width: '120px', padding: '6px 10px', fontSize: '0.9rem', textAlign: 'right' }}
                          value={m.actual}
                          onChange={(e) => handleUpdateMonday(mondayModalStream.id, idx, e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bt-modal-footer">
                <button className="bt-btn-primary" onClick={() => setMondayModalStream(null)}>
                  Done & Update Ledger
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default IncomeSheet;
